/**
 * Modal dialog for the Assignment-related component for Quick Assign link copying.
 **/
import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";

import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {ModalDialog, ModalPanel} from "@khanacademy/wonder-blocks-modal";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    Body,
    HeadingXSmall,
    Tagline,
} from "@khanacademy/wonder-blocks-typography";
import {usePublishAnalyticsEvent} from "@khan/analytics-learning-time";
import type {Assignment} from "@khan/assignments-types";
import {staticUrl, useLocation} from "@khan/urls";

import CopyableTextInput from "#class-creator-package/components/copyable-text-input.tsx";
import {getContentKindLabelMap} from "#class-package/assignments/assignment-progress/assignment-progress-header-cell.tsx";

import linkIcon from "@phosphor-icons/core/regular/link.svg";

export type AssignmentData = {
    id: string | null | undefined;
    classroomName?: string | null | undefined;
    contentTitle?: string | null | undefined;
    title?: string | null | undefined;
    contentKind?: string | null | undefined;
};

export type ActionType = "ASSIGN" | "EDIT" | "COPY" | "SHARE";
interface Props {
    assignments: Array<Assignment>;
    cedarActionType: ActionType;
}

export const AssignmentCopyLinkDialog = ({
    assignments,
    cedarActionType,
}: Props) => {
    const location = useLocation();
    // Publish the analytics event when the link is copied.
    const publishAnalyticsEvent = usePublishAnalyticsEvent();
    const copyText = () => {
        publishAnalyticsEvent({
            eventType: "TeacherAssignmentCopyLinkClicked",
            actionType: cedarActionType,
            eventSchemaVersion: 1,
        });
    };
    const modalDescriptionId = "assignment-link-modal-description-0";

    const modalTitle = t`Share the assignment with students`;

    let modalDescription = "";
    const modalDescriptionBase = t`You can always find assignment links again in your Manage tab.`;
    const modalDescriptionMoreThanFive = t`Only the first five links are shown. You can always find assignment links again in your Manage tab.`;
    const modalDescriptionMultiClassMultiAssignments = t`You can always find assignment links in your Manage tab.`;

    const errorMessage = t`Sorry, there was an error getting the assignment information.`;
    let linkContent: React.ReactElement = <Body>{errorMessage}</Body>;

    // Group the assignments by content ID. We will use this to determine if we are in the
    // special case of having multiple assignments for multiple classes.
    const groupedAssignments = assignments.reduce(
        (acc, assignment) => {
            if (assignment.contents && assignment.contents.length > 0) {
                const key = assignment?.contents[0]?.id || "";
                if (!acc[key]) {
                    acc[key] = [];
                }
                acc[key].push(assignment);
            }
            return acc;
        },
        {} as Record<string, Assignment[]>,
    );

    // Check for the special case of having myultiple assignments for multiple classes.
    const multiClassMultiAssignments =
        groupedAssignments &&
        Object.keys(groupedAssignments).length > 1 && // More than one assignment was made
        Object.values(groupedAssignments).some((group) => group.length > 1);

    // Build the link content.
    // For the special case of multiple assignments for multiple classes, show no links
    // and use the modal description for this use case.
    // For all other cases, only show the first five links.
    if (multiClassMultiAssignments) {
        linkContent = <></>;
        modalDescription = modalDescriptionMultiClassMultiAssignments;
    } else {
        const assignmentData = assignments.map((createdAssignment) => {
            return {
                id: createdAssignment.id,
                title: createdAssignment.title,
                contentTitle:
                    createdAssignment.contents &&
                    createdAssignment.contents.length > 0
                        ? createdAssignment.contents[0].title
                        : null,
                classroomName: createdAssignment.studentList?.name,
                contentKind:
                    createdAssignment.contents &&
                    createdAssignment.contents.length > 0
                        ? createdAssignment.contents[0].kind
                        : null,
            };
        });

        // Determine which modal description to use based on the number of assignments.
        if (assignmentData.length > 5) {
            modalDescription = modalDescriptionMoreThanFive;
        } else {
            modalDescription = modalDescriptionBase;
        }

        switch (assignmentData.length) {
            case 0:
                break;
            case 1:
                linkContent = (
                    <CopyableTextInput
                        testId="assignment-link-for-students"
                        value={`${location.origin}/assignment/${assignments[0].id}`}
                        buttonKind="primary"
                        stackOnMobile={true}
                        startIcon={linkIcon}
                        copyText={copyText}
                    />
                );
                break;
            default:
                const contentKindLabelMap = getContentKindLabelMap();

                const links = assignmentData.slice(0, 5).map((assignment) => (
                    <>
                        {(assignment.title || assignment.contentTitle) && (
                            <>
                                <HeadingXSmall>
                                    {assignment.classroomName}
                                    {" \u22C5 "}
                                    {assignment.contentKind
                                        ? `${contentKindLabelMap[assignment.contentKind]}  \u22C5 `
                                        : ""}
                                    {assignment.title ||
                                        assignment.contentTitle}
                                </HeadingXSmall>
                                <Strut size={spacing.xxxSmall_4} />
                            </>
                        )}
                        <CopyableTextInput
                            testId="assignment-link-for-students"
                            value={`${location.origin}/assignment/${assignment.id}`}
                            buttonKind="primary"
                            stackOnMobile={true}
                            startIcon={linkIcon}
                            copyText={copyText}
                        />
                        <Strut size={spacing.medium_16} />
                    </>
                ));

                linkContent = <>{links}</>;
                break;
        }
    }

    const copyLinkContent = (
        <View style={styles.content}>
            <View style={styles.row}>
                <View style={styles.columnLeft}>
                    <img
                        className={css(styles.khanmigoBlushing)}
                        src={staticUrl(
                            "/images/khanmigo/blushing_khanmigo-2x.png",
                        )}
                        alt="khanmigo-blushing"
                    />
                </View>
                <Strut size={spacing.xLarge_32} />
                <View style={styles.columnRight}>
                    <Tagline id={modalDescriptionId}>{modalTitle}</Tagline>
                    <Strut size={spacing.medium_16} />
                    <Body>{modalDescription}</Body>
                    <Strut size={spacing.medium_16} />
                    {linkContent}
                </View>
            </View>
        </View>
    );

    return (
        <ModalDialog
            aria-labelledby={modalDescriptionId}
            style={styles.container}
        >
            <ModalPanel content={copyLinkContent} />
        </ModalDialog>
    );
};

const styles = StyleSheet.create({
    container: {
        height: "auto",
        maxWidth: 800,
    },
    content: {
        width: "fitContent",
        height: "fitContent",
    },
    columnLeft: {
        width: "10%",
    },
    columnRight: {
        width: "70%",
    },
    khanmigoBlushing: {
        width: 50,
        transform: "rotate(-20deg)",
    },
    row: {
        flexDirection: "row",
        alignItems: "top",
    },
});
